import React, { createContext, useContext, useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import { dispatchSessionEnded } from '../services/sessionManager';

// Create a context for the socket
const SocketContext = createContext(null);

// Custom hook to use the socket context
export const useSocket = () => useContext(SocketContext);

// SocketProvider component
export const SocketProvider = ({ children, token }) => {
    const [socket, setSocket] = useState(null);

    useEffect(() => {
        if (token) {
            // Connect to the server. Ensure the URL is correct.
            // Using REACT_APP_API_URL for the base URL, then stripping /api or ensuring it's the root for socket
            let socketURL = process.env.REACT_APP_API_URL || 'http://localhost:5001';
            // If REACT_APP_API_URL includes '/api', remove it for the socket connection,
            // as Socket.IO usually connects to the root of the server.
            if (socketURL.endsWith('/api')) {
                socketURL = socketURL.substring(0, socketURL.length - 4);
            }
            
            console.log(`Attempting to connect socket to: ${socketURL} with token.`);
            const newSocket = io(socketURL, {
                auth: { token },
                transports: ['websocket'] // Best for modern browsers, helps avoid some proxy issues
            });

            newSocket.on('connect', () => {
                console.log(`Socket connected: ${newSocket.id}`);
                setSocket(newSocket);
            });

            newSocket.on('disconnect', (reason) => {
                console.log('Socket disconnected:', reason);
                // Optionally, attempt to reconnect or notify user
                if (reason === 'io server disconnect') {
                    // The server deliberately disconnected the socket, probably auth issue
                    newSocket.connect(); // Or handle more gracefully
                }
                setSocket(null); // Clear socket on disconnect
            });

            newSocket.on('connect_error', (err) => {
                console.error('Socket connection error:', err.message, err.data || '');
                // The server refuses a socket whose session has ended (security batch 2).
                if (/session ended/i.test(err.message || '')) {
                    dispatchSessionEnded('SESSION_EXPIRED');
                    newSocket.disconnect();
                }
            });

            newSocket.on('session_ended', (data) => {
                dispatchSessionEnded((data && data.code) || 'SESSION_EXPIRED');
            });
            
            // For debugging: log all events received by this socket instance
            // newSocket.onAny((eventName, ...args) => {
            //   console.log(`Socket event received: ${eventName}`, args);
            // });

            // Cleanup on token change or component unmount
            return () => {
                console.log('Disconnecting socket due to token change or unmount...');
                newSocket.disconnect();
                setSocket(null);
            };
        } else if (socket) {
            // If token is removed (e.g., logout) but socket instance still exists, disconnect it
            console.log('Token removed, disconnecting existing socket...');
            socket.disconnect();
            setSocket(null);
        }
    }, [token]); // Effect dependencies: re-run if token changes

    return (
        <SocketContext.Provider value={socket}>
            {children}
        </SocketContext.Provider>
    );
};
